import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useAdminOrders, useKitchenStations } from '../../lib/admin-hooks';
import { useTableManagement } from '../../lib/table-hooks';
import { PayTableModal } from '../../components/admin/PayTableModal';
import { CabanaModifyOrderModal } from '../../components/admin/CabanaModifyOrderModal';
import { CabanaAddItemsModal } from '../../components/admin/CabanaAddItemsModal';
import { VoidConfirmModal } from '../../components/admin/VoidConfirmModal';
import { logVoidAudit, updateVoidedColumns } from '../../lib/void-audit';
import type { ActionResult } from '../../lib/admin-hooks';
import type { TableWithOrder } from '../../lib/types';
import { useTenant } from '../../lib/tenant-context';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { Clock, CheckCircle2, AlertTriangle, ShoppingBag, Bike, Store, ChefHat, X, Receipt, Utensils, ShoppingBasket, Printer, Pencil } from 'lucide-react';
interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}
export function CajaOverviewPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const {
    stations,
    loading: stationsLoading
  } = useKitchenStations(restaurant?.id);
  const {
    orders,
    loading: ordersLoading,
    acceptOrder,
    startOrderPreparation,
    markOrderReady,
    deliverOrder,
    cancelOrder,
    refetchOrders
  } = useAdminOrders(restaurant?.id);
  const {
    tables,
    refetch: refetchTables
  } = useTableManagement(restaurant?.id);
  const [updatingOrders, setUpdatingOrders] = useState<Set<string>>(new Set());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'new' | 'preparing' | 'ready' | 'delayed'>('all');
  const [showPayModal, setShowPayModal] = useState(false);
  const [payTable, setPayTable] = useState<TableWithOrder | null>(null);
  const isCapricho = String(restaurant?.id) === '2' || restaurant?.slug === 'capricho-de-canela';
  const isCongelados = String(restaurant?.id) === '18' || restaurant?.slug === 'heladeria-congelados';
  const isNatucafe = String(restaurant?.id) === '19' || restaurant?.slug === 'natucafe-coffee-shop';
  // Rancho San Francisco (id 28) — Control de Caja muestra pedidos
  // bucketeados por tipo: Mesas (dine_in), Para llevar (pickup),
  // Express (delivery). Cobrar/Facturar y la impresión siguen
  // funcionando por las rutas existentes (PayTableModal + processPayment).
  const isRanchoSanFrancisco = String(restaurant?.id) === '28' || restaurant?.slug === 'rancho-san-francisco';
  // La Cabaña Parrillera (restaurant_id 24) — exclusive feature:
  // cashier can modify or cancel any active order from this page.
  const isCabanaParrillera = String(restaurant?.id) === '24' || restaurant?.slug === 'la-cabana-parrillera' || restaurant?.slug === 'cabana-parrillera';
  const [modifyOrderId, setModifyOrderId] = useState<string | null>(null);
  const [addItemsOrderId, setAddItemsOrderId] = useState<string | null>(null);
  const isSimplifiedFlow = isCapricho || isCongelados;
  const [channelFilter, setChannelFilter] = useState<'all' | 'marketplace' | 'restaurant'>('all');
  const [ranchoBucket, setRanchoBucket] = useState<'all' | 'mesas' | 'pickup' | 'delivery'>('all');
  // Rancho — set of order ids currently being printed (to disable the
  // "Imprimir comanda" button while the insert is in flight).
  const [printingOrderIds, setPrintingOrderIds] = useState<Set<string>>(new Set());
  // Rancho — order id currently open in the modify modal.
  const [ranchoModifyOrderId, setRanchoModifyOrderId] = useState<string | null>(null);
  // Void Auth State for NatuCafe order cancellation
  const [voidModalState, setVoidModalState] = useState<{
    isOpen: boolean;
    orderId: string | null;
    originalAmount: number;
  }>({
    isOpen: false,
    orderId: null,
    originalAmount: 0
  });
  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, {
      id,
      type,
      message
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3500);
  }, []);
  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  const handleOpenPay = useCallback((order: any) => {
    // Marketplace orders (delivery / pickup) have no table_id. We still
    // want to allow Cobrar/Facturar from caja for them — build a
    // synthetic "table" referencing the order so PayTableModal can load
    // the items by order_id and process payment normally.
    if (!order?.table_id) {
      setPayTable({
        id: '',
        restaurant_id: String(restaurant?.id || ''),
        // Prefix marks this as a non-table sentinel so the modal header
        // shows "Pedido #..." instead of "Mesa ...".
        table_number: `order:${order.id}`,
        table_name: order.delivery_type === 'delivery' ? `Pedido Delivery #${String(order.order_number || '').split('-').pop() || ''}` : order.delivery_type === 'pickup' ? `Pedido para Llevar #${String(order.order_number || '').split('-').pop() || ''}` : `Pedido #${String(order.order_number || '').split('-').pop() || ''}`,
        zone: null,
        capacity: null,
        qr_code_url: null,
        qr_token: null,
        is_active: true,
        created_at: '',
        updated_at: '',
        current_order: order,
        current_order_items: order.items || [],
        status: 'occupied',
        order_channel: 'marketplace',
        time_open_minutes: null,
        item_count: (order.items || []).length
      } as TableWithOrder);
      setShowPayModal(true);
      return;
    }
    const matched = tables.find(t => String(t.id) === String(order.table_id));
    if (matched) {
      setPayTable(matched);
    } else {
      setPayTable({
        id: String(order.table_id),
        restaurant_id: String(restaurant?.id || ''),
        table_number: order.table_id,
        table_name: null,
        zone: null,
        capacity: null,
        qr_code_url: null,
        qr_token: null,
        is_active: true,
        created_at: '',
        updated_at: '',
        current_order: order,
        current_order_items: order.items || [],
        status: 'occupied',
        order_channel: order.source === 'waiter' ? 'waiter' : 'qr',
        time_open_minutes: null,
        item_count: (order.items || []).length
      } as TableWithOrder);
    }
    setShowPayModal(true);
  }, [tables, restaurant?.id, addToast]);
  // ─── Rancho San Francisco — Imprimir comanda desde Control de Caja ─
  // Inserta una fila pending en order_print_jobs con un trigger único
  // (manual_caja_<timestamp>) para que el bridge Python imprima el
  // ticket. Resuelve tracking_token (UUID) si la orden trae id BIGINT,
  // igual que en TableManagementPage. Sólo Rancho — cero impacto en el
  // resto. La caja y el pago siguen funcionando idénticos.
  const handleImprimirComandaRancho = useCallback(async (order: any) => {
    if (!isRanchoSanFrancisco) return;
    if (!order?.id) return;
    setPrintingOrderIds(prev => new Set(prev).add(String(order.id)));
    try {
      // Resolver tracking_token (UUID) — order_print_jobs.order_id es UUID.
      let orderRefUuid: string | null = order.tracking_token ?? null;
      if (!orderRefUuid && order.id) {
        try {
          const {
            data: row
          } = await supabase.from('orders').select('tracking_token').eq('id', order.id).maybeSingle();
          orderRefUuid = (row as any)?.tracking_token ?? null;
        } catch (e) {
          console.warn('[RANCHO_PRINT][caja] tracking_token fetch failed (non-blocking):', e);
        }
      }
      if (!orderRefUuid) orderRefUuid = order.id ?? null;
      if (!orderRefUuid) {
        addToast('error', 'Orden sin identificador (no se puede imprimir)');
        return;
      }
      const triggerValue = `manual_caja_${Date.now()}`;
      const deliveryType = order.delivery_type || 'dine_in';
      const customerName = order.customer_name || (deliveryType === 'dine_in' && order.table_id ? `Mesa ${order.table_id}` : 'Cliente');
      // Rancho — Enriquecemos cada ítem del payload con (a) las opciones
      // seleccionadas concatenadas al nombre ("Casado · Chicharrón") y
      // (b) un prefijo "[Arma]" en los ítems de Arma tu plato
      // (menu_item_id NULL) para que NO se mezclen visualmente con
      // platillos normales (ej. "Kilo de chicharrones") en la comanda
      // impresa. El bridge imprime item_name_snapshot tal cual.
      const enrichedItems = (order.items ?? []).map((it: any) => {
        const baseName = String(it.item_name_snapshot || it.item_name || '').trim();
        const itemOptions = Array.isArray(it.options) ? it.options : [];
        const optsSummary = itemOptions.map((o: any) => String(o.option_name_snapshot || o.option_name || '').trim()).filter((s: string) => s.length > 0).join(' · ');
        const isArma = it.menu_item_id == null;
        const armaPrefix = isArma ? '[Arma] ' : '';
        const enrichedName = optsSummary ? `${armaPrefix}${baseName} · ${optsSummary}` : `${armaPrefix}${baseName}`;
        return {
          ...it,
          is_arma_tu_plato: isArma,
          original_item_name: baseName,
          item_name_snapshot: enrichedName
        };
      });
      const {
        error
      } = await supabase.from('order_print_jobs').insert({
        restaurant_id: 28,
        order_id: orderRefUuid,
        order_number: order.order_number ?? null,
        status: 'pending',
        payload: {
          order,
          order_internal_id: order.id ?? null,
          order_number: order.order_number ?? null,
          created_at: order.created_at ?? null,
          customer_name: customerName,
          customer_phone: order.customer_phone ?? null,
          delivery_type: deliveryType,
          payment_method: order.payment_method ?? 'cash',
          table_number: order.table_id ?? null,
          subtotal: order.subtotal ?? 0,
          delivery_fee: order.delivery_fee ?? 0,
          total: order.total ?? 0,
          notes: order.notes ?? null,
          items: enrichedItems,
          trigger: triggerValue
        }
      });
      if (error) {
        console.error('[RANCHO_PRINT][caja] insert error:', error);
        addToast('error', `No se pudo imprimir: ${error.message || 'error desconocido'}`);
      } else {
        addToast('success', '🖨 Enviado a la impresora');
      }
    } catch (e: any) {
      console.error('[RANCHO_PRINT][caja] exception:', e);
      addToast('error', e?.message || 'Error al enviar a la impresora');
    } finally {
      setPrintingOrderIds(prev => {
        const next = new Set(prev);
        next.delete(String(order.id));
        return next;
      });
    }
  }, [isRanchoSanFrancisco, addToast]);
  if (restLoading || !restaurant) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-2836">
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-2837">
          <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" data-id="element-2838" />
        </div>
      </AdminLayout>;
  }
  // Filter active orders
  const activeOrders = orders.filter((o: any) => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status));
  // Channel classification (NatuCafé)
  const isMarketplaceOrder = (o: any) => o.delivery_type === 'delivery' || o.delivery_type === 'pickup';
  const isRestaurantOrder = (o: any) => o.delivery_type === 'dine_in' || !isMarketplaceOrder(o) && (o.table_id || o.source === 'waiter' || o.source === 'qr');
  const marketplaceCount = activeOrders.filter(isMarketplaceOrder).length;
  const restaurantCount = activeOrders.filter(isRestaurantOrder).length;
  // Rancho — bucket classification by delivery_type
  const isRanchoMesaOrder = (o: any) => o.delivery_type === 'dine_in' || o.delivery_type === 'mesa' || !o.delivery_type && o.table_id;
  const isRanchoPickupOrder = (o: any) => o.delivery_type === 'pickup';
  const isRanchoDeliveryOrder = (o: any) => o.delivery_type === 'delivery';
  const ranchoMesasCount = activeOrders.filter(isRanchoMesaOrder).length;
  const ranchoPickupCount = activeOrders.filter(isRanchoPickupOrder).length;
  const ranchoDeliveryCount = activeOrders.filter(isRanchoDeliveryOrder).length;
  const channeledOrders = isNatucafe ? activeOrders.filter((o: any) => {
    if (channelFilter === 'all') return true;
    if (channelFilter === 'marketplace') return isMarketplaceOrder(o);
    if (channelFilter === 'restaurant') return isRestaurantOrder(o);
    return true;
  }) : isRanchoSanFrancisco ? activeOrders.filter((o: any) => {
    if (ranchoBucket === 'all') return true;
    if (ranchoBucket === 'mesas') return isRanchoMesaOrder(o);
    if (ranchoBucket === 'pickup') return isRanchoPickupOrder(o);
    if (ranchoBucket === 'delivery') return isRanchoDeliveryOrder(o);
    return true;
  }) : activeOrders;
  const sortedOrders = [...channeledOrders].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const newOrders = channeledOrders.filter(o => o.status === 'new' || o.status === 'accepted');
  const preparingOrders = channeledOrders.filter(o => o.status === 'preparing');
  const readyOrders = channeledOrders.filter(o => o.status === 'ready' || o.status === 'searching_driver');
  const delayedOrders = channeledOrders.filter(o => {
    const waitMinutes = Math.floor((new Date().getTime() - new Date(o.created_at).getTime()) / 60000);
    return waitMinutes >= 15;
  });
  const getShortOrderNumber = (orderNumber: string) => {
    if (!orderNumber) return '';
    const parts = orderNumber.split('-');
    return parts[parts.length - 1] || orderNumber;
  };
  // Action handler — now with feedback
  const handleOrderAction = async (orderId: string, action: string) => {
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    let result: ActionResult = {
      success: false,
      error: 'Acción desconocida'
    };
    try {
      switch (action) {
        case 'send_to_kitchen':
          {
            // Combined: accept + start preparation in one action
            result = await acceptOrder(orderId);
            if (result.success) {
              result = await startOrderPreparation(orderId);
            }
            if (result.success) addToast('success', '🔥 Enviado a cocina');
            break;
          }
        case 'start_preparation':
          result = await startOrderPreparation(orderId);
          if (result.success) addToast('success', '🔥 Enviado a cocina');
          break;
        case 'mark_ready':
          result = await markOrderReady(orderId);
          if (result.success) addToast('success', '✅ Pedido marcado como listo');
          break;
        case 'deliver':
          result = await deliverOrder(orderId);
          if (result.success) addToast('success', '🚀 Pedido entregado');
          break;
        case 'capricho_receive':
          {
            // Combined: accept + start preparation
            result = await acceptOrder(orderId);
            if (result.success) {
              result = await startOrderPreparation(orderId);
            }
            if (result.success) addToast('success', '✅ Pedido recibido');
            break;
          }
        case 'capricho_complete':
          {
            // Combined: mark ready + deliver
            result = await markOrderReady(orderId);
            if (result.success) {
              result = await deliverOrder(orderId);
            }
            if (result.success) addToast('success', '🎉 Pedido completado');
            break;
          }
        case 'cancel':
          if (isNatucafe) {
            const orderToCancel = orders.find((o: any) => String(o.id) === String(orderId));
            setVoidModalState({
              isOpen: true,
              orderId,
              originalAmount: orderToCancel?.total || 0
            });
            // Return early, the actual cancellation will happen in handleVoidConfirm
            setUpdatingOrders(prev => {
              const next = new Set(prev);
              next.delete(orderId);
              return next;
            });
            return;
          }
          if (confirm('¿Estás seguro de cancelar este pedido?')) {
            result = await cancelOrder(orderId);
            if (result.success) addToast('success', '❌ Pedido cancelado');
          } else {
            result = {
              success: true
            };
          }
          break;
      }
      if (!result.success && result.error) {
        addToast('error', result.error);
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error inesperado');
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  const handleVoidConfirm = async (authorizedBy: string, reason: string) => {
    if (!voidModalState.orderId || !restaurant?.id) return;
    const orderId = voidModalState.orderId;
    setVoidModalState({
      isOpen: false,
      orderId: null,
      originalAmount: 0
    });
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    try {
      await updateVoidedColumns('orders', Number(orderId), authorizedBy, reason);
      await logVoidAudit({
        restaurant_id: restaurant.id,
        order_id: Number(orderId),
        action_type: 'cancel_order',
        authorized_by: authorizedBy,
        reason,
        original_amount: voidModalState.originalAmount
      });
      const result = await cancelOrder(orderId);
      if (result.success) {
        addToast('success', '❌ Pedido anulado correctamente');
      } else if (result.error) {
        addToast('error', result.error);
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error al anular pedido');
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-2839">
      <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans" data-id="element-2840">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-2841">
          <div data-id="element-2842">
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3" data-id="element-2843">
              Control de Caja
              <span className="bg-white/10 text-white/70 px-3 py-1 rounded-full text-sm font-bold tabular-nums" data-id="element-2844">
                {activeOrders.length} activos
              </span>
            </h1>
            <p className="text-white/50 text-sm mt-1" data-id="element-2845">
              Vista general de todas las estaciones y pedidos en curso
            </p>
          </div>
        </div>

        {/* NatuCafé channel tabs */}
        {isNatucafe && <div className="mb-6 flex flex-wrap gap-2 p-1.5 bg-[#141414] border border-white/5 rounded-2xl w-full sm:w-auto" data-id="element-2846">
            {([{
          key: 'all',
          label: 'Todos',
          count: activeOrders.length,
          icon: null
        }, {
          key: 'marketplace',
          label: 'Marketplace',
          count: marketplaceCount,
          icon: Bike
        }, {
          key: 'restaurant',
          label: 'En el restaurante',
          count: restaurantCount,
          icon: Store
        }] as const).map(tab => {
          const isActive = channelFilter === tab.key;
          const Icon = tab.icon;
          return <button key={tab.key} onClick={() => setChannelFilter(tab.key)} className={cn('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex-1 sm:flex-none justify-center', isActive ? 'bg-white text-black shadow-md' : 'text-white/60 hover:text-white hover:bg-white/5')} data-id="element-2847">
                  {Icon && <Icon className="w-4 h-4" data-id="element-2848" />}
                  <span data-id="element-2849">{tab.label}</span>
                  <span className={cn('min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-black flex items-center justify-center tabular-nums', isActive ? 'bg-black/10 text-black' : 'bg-white/10 text-white/70')} data-id="element-2850">
                    {tab.count}
                  </span>
                </button>;
        })}
          </div>}

        {/* Rancho San Francisco — bucket tabs (Mesas / Para llevar / Express) */}
        {isRanchoSanFrancisco && <div className="mb-6 flex flex-wrap gap-2 p-1.5 bg-[#141414] border border-white/5 rounded-2xl w-full sm:w-auto" data-id="element-2851">
            {([{
          key: 'all',
          label: 'Todos',
          count: activeOrders.length,
          icon: null
        }, {
          key: 'mesas',
          label: 'Mesas',
          count: ranchoMesasCount,
          icon: Utensils
        }, {
          key: 'pickup',
          label: 'Para llevar',
          count: ranchoPickupCount,
          icon: ShoppingBasket
        }, {
          key: 'delivery',
          label: 'Express',
          count: ranchoDeliveryCount,
          icon: Bike
        }] as const).map(tab => {
          const isActive = ranchoBucket === tab.key;
          const Icon = tab.icon;
          return <button key={tab.key} onClick={() => setRanchoBucket(tab.key)} className={cn('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex-1 sm:flex-none justify-center', isActive ? 'bg-white text-black shadow-md' : 'text-white/60 hover:text-white hover:bg-white/5')} data-id="element-2852">
                  {Icon && <Icon className="w-4 h-4" data-id="element-2853" />}
                  <span data-id="element-2854">{tab.label}</span>
                  <span className={cn('min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-black flex items-center justify-center tabular-nums', isActive ? 'bg-black/10 text-black' : 'bg-white/10 text-white/70')} data-id="element-2855">
                    {tab.count}
                  </span>
                </button>;
        })}
          </div>}

        {/* Stats Row — clickable filters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8" data-id="element-2856">
          <button onClick={() => setActiveFilter(activeFilter === 'new' ? 'all' : 'new')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-blue-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'new' ? 'border-blue-500 ring-2 ring-blue-500/40 bg-blue-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-2857">
            <div className="flex items-center justify-between mb-2" data-id="element-2858">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-2859">
                <ShoppingBag className="w-4 h-4 text-blue-400" data-id="element-2860" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-2861">
                {newOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-2862">Pendientes</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'preparing' ? 'all' : 'preparing')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-amber-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'preparing' ? 'border-amber-500 ring-2 ring-amber-500/40 bg-amber-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-2863">
            <div className="flex items-center justify-between mb-2" data-id="element-2864">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center" data-id="element-2865">
                <ChefHat className="w-4 h-4 text-amber-400" data-id="element-2866" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-2867">
                {preparingOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-2868">En Cocina</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'ready' ? 'all' : 'ready')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-emerald-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'ready' ? 'border-emerald-500 ring-2 ring-emerald-500/40 bg-emerald-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-2869">
            <div className="flex items-center justify-between mb-2" data-id="element-2870">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center" data-id="element-2871">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" data-id="element-2872" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-2873">
                {readyOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-2874">Listos</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'delayed' ? 'all' : 'delayed')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-red-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'delayed' ? 'border-red-500 ring-2 ring-red-500/40 bg-red-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-2875">
            <div className="flex items-center justify-between mb-2" data-id="element-2876">
              <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center" data-id="element-2877">
                <AlertTriangle className="w-4 h-4 text-red-400" data-id="element-2878" />
              </div>
              <span className="text-2xl font-black tabular-nums text-red-400" data-id="element-2879">
                {delayedOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-red-400/70" data-id="element-2880">
              Atrasados (&gt;15m)
            </p>
          </button>
        </div>

        {/* Active filter indicator */}
        {activeFilter !== 'all' && <div className="flex items-center gap-2 mb-4" data-id="element-2881">
            <span className="text-sm text-white/50" data-id="element-2882">Filtrando por:</span>
            <span className={cn('px-3 py-1 rounded-full text-xs font-bold', activeFilter === 'new' && 'bg-blue-500/20 text-blue-400', activeFilter === 'preparing' && 'bg-amber-500/20 text-amber-400', activeFilter === 'ready' && 'bg-emerald-500/20 text-emerald-400', activeFilter === 'delayed' && 'bg-red-500/20 text-red-400')} data-id="element-2883">
              {activeFilter === 'new' && `Pendientes (${newOrders.length})`}
              {activeFilter === 'preparing' && `En Cocina (${preparingOrders.length})`}
              {activeFilter === 'ready' && `Listos (${readyOrders.length})`}
              {activeFilter === 'delayed' && `Atrasados (${delayedOrders.length})`}
            </span>
            <button onClick={() => setActiveFilter('all')} className="text-white/30 hover:text-white/60 transition-colors" data-id="element-2884">
              <X className="w-4 h-4" data-id="element-2885" />
            </button>
          </div>}

        {/* Orders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 items-start" data-id="element-2886">
          {ordersLoading || stationsLoading ? [1, 2, 3, 4].map(i => <div key={i} className="h-64 rounded-2xl bg-[#141414] animate-pulse border border-white/5" data-id="element-2887" />) : (() => {
          const filteredOrders = sortedOrders.filter((order: any) => {
            if (activeFilter === 'all') return true;
            if (activeFilter === 'new') return order.status === 'new' || order.status === 'accepted';
            if (activeFilter === 'preparing') return order.status === 'preparing';
            if (activeFilter === 'ready') return order.status === 'ready' || order.status === 'searching_driver';
            if (activeFilter === 'delayed') {
              const waitMinutes = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
              return waitMinutes >= 15;
            }
            return true;
          });
          return filteredOrders.length > 0 ? filteredOrders.map((order: any) => {
            const isUpdatingOrder = updatingOrders.has(order.id);
            const waitMinutes = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
            const stationProgress = new Map<string, {
              total: number;
              ready: number;
              name: string;
            }>();
            order.items.forEach((item: any) => {
              const stationCode = item.station_code || 'sin_asignar';
              const stationName = stations.find(s => s.code === stationCode)?.name || (stationCode === 'sin_asignar' ? 'General' : stationCode);
              if (!stationProgress.has(stationCode)) {
                stationProgress.set(stationCode, {
                  total: 0,
                  ready: 0,
                  name: stationName
                });
              }
              const progress = stationProgress.get(stationCode)!;
              progress.total += item.quantity;
              if (item.status === 'ready' || item.status === 'served') {
                progress.ready += item.quantity;
              }
            });
            const allItemsReady = Array.from(stationProgress.values()).every(p => p.ready === p.total) && order.items.length > 0;
            // Calculate overall progress percentage
            const totalItems = Array.from(stationProgress.values()).reduce((sum, p) => sum + p.total, 0);
            const readyItems = Array.from(stationProgress.values()).reduce((sum, p) => sum + p.ready, 0);
            const overallPercentage = totalItems > 0 ? Math.round(readyItems / totalItems * 100) : 0;
            // Kitchen activity: any item has been touched by KDS
            // (waiter/dine_in orders skip 'preparing' status — items get
            //  worked on while order.status stays 'new')
            const hasKitchenActivity = order.items.some((it: any) => it.status === 'ready' || it.status === 'served' || it.status === 'in_progress' || it.status === 'preparing');
            const showProgressView = order.status === 'preparing' || order.status === 'ready' || hasKitchenActivity;
            let borderColor = 'border-white/10';
            if (order.status === 'new' || order.status === 'accepted') borderColor = 'border-blue-500';else if (order.status === 'preparing') borderColor = 'border-amber-500';else if (order.status === 'ready' || order.status === 'searching_driver' || allItemsReady) borderColor = 'border-emerald-500';
            // ── Simplified card (Capricho / Congelados) ──
            if (isSimplifiedFlow) {
              const caprichoStatus = order.status === 'new' || order.status === 'accepted' ? 'nuevo' : order.status === 'preparing' ? 'en_preparacion' : order.status === 'ready' || order.status === 'searching_driver' ? 'listo' : 'otro';
              return <div key={order.id} className={cn('bg-[#141414] rounded-2xl flex flex-col border-l-4 border-y border-r border-white/5 overflow-hidden shadow-xl transition-all', caprichoStatus === 'nuevo' ? 'border-blue-500' : caprichoStatus === 'en_preparacion' ? 'border-amber-500' : caprichoStatus === 'listo' ? 'border-emerald-500' : 'border-white/10')} data-id="element-2888">
                          {/* Header */}
                          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-white/[0.02]" data-id="element-2889">
                            <div className="flex items-center gap-2" data-id="element-2890">
                              <span className="text-lg font-black text-white tabular-nums" data-id="element-2891">
                                #{getShortOrderNumber(order.order_number)}
                              </span>
                              {order.delivery_type === 'delivery' ? <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-2892">
                                  <Bike className="w-3 h-3" data-id="element-2893" /> Delivery
                                </span> : <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-2894">
                                  <ShoppingBag className="w-3 h-3" data-id="element-2895" /> Llevar
                                </span>}
                            </div>
                            <span className={cn('text-xl font-black tabular-nums', waitMinutes >= 15 ? 'text-red-500' : waitMinutes >= 10 ? 'text-amber-500' : 'text-emerald-500')} data-id="element-2896">
                              {waitMinutes}m
                            </span>
                          </div>

                          {/* Customer */}
                          <div className="px-4 py-3 border-b border-white/5" data-id="element-2897">
                            <p className="text-base font-black text-white" data-id="element-2898">
                              {order.customer_name}
                            </p>
                            {order.customer_phone && <p className="text-xs text-white/40 mt-0.5" data-id="element-2899">
                                {order.customer_phone}
                              </p>}
                            <p className="text-sm font-black text-[#C8A96A] mt-1" data-id="element-2900">
                              ₡{order.total.toLocaleString()}
                            </p>
                          </div>

                          {/* Status */}
                          <div className={cn('px-4 py-2.5 border-b', caprichoStatus === 'nuevo' ? 'bg-blue-500/10 border-blue-500/20' : caprichoStatus === 'en_preparacion' ? 'bg-amber-500/10 border-amber-500/20' : caprichoStatus === 'listo' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-white/5 border-white/5')} data-id="element-2901">
                            <span className={cn('text-xs font-black uppercase tracking-wider flex items-center gap-1.5', caprichoStatus === 'nuevo' ? 'text-blue-400' : caprichoStatus === 'en_preparacion' ? 'text-amber-400' : caprichoStatus === 'listo' ? 'text-emerald-400' : 'text-white/40')} data-id="element-2902">
                              {caprichoStatus === 'nuevo' && <>
                                  <Clock className="w-3.5 h-3.5" data-id="element-2903" /> Nuevo pedido
                                </>}
                              {caprichoStatus === 'en_preparacion' && <>
                                  <ChefHat className="w-3.5 h-3.5" data-id="element-2904" /> En
                                  preparación
                                </>}
                              {caprichoStatus === 'listo' && <>
                                  <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2905" /> Listo
                                  para entregar
                                </>}
                            </span>
                          </div>

                          {/* Items with toppings — excludes items cancelled
                                            via the modify modal so the comanda matches the
                                            real state of the order. */}
                          <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-2906">
                            <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-2907">
                              Detalle del pedido
                            </h4>
                            <div className="space-y-2.5" data-id="element-2908">
                              {order.items.filter((item: any) => item.status !== 'cancelled').map((item: any) => <div key={item.id} data-id="element-2909">
                                    <div className="flex justify-between text-sm" data-id="element-2910">
                                      <span className="text-white font-bold" data-id="element-2911">
                                        {item.quantity}x{' '}
                                        {item.item_name_snapshot}
                                      </span>
                                      <span className="text-[#C8A96A] font-bold text-xs shrink-0" data-id="element-2912">
                                        ₡
                                        {(item.unit_price * item.quantity).toLocaleString()}
                                      </span>
                                    </div>
                                    {item.options && item.options.length > 0 && <div className="mt-1 ml-4 space-y-0.5" data-id="element-2913">
                                          {item.options.map((opt: any, idx: number) => <div key={idx} className="flex justify-between text-xs" data-id="element-2914">
                                                <span className="text-amber-400/80" data-id="element-2915">
                                                  + {opt.option_name}
                                                </span>
                                                {opt.price_delta > 0 && <span className="text-white/30" data-id="element-2916">
                                                    +₡
                                                    {opt.price_delta.toLocaleString()}
                                                  </span>}
                                              </div>)}
                                        </div>}
                                    {item.notes && <p className="text-xs text-white/30 italic mt-1 ml-4" data-id="element-2917">
                                        📝 {item.notes}
                                      </p>}
                                  </div>)}
                            </div>
                            {order.notes && <div className="mt-3 pt-3 border-t border-white/5" data-id="element-2918">
                                <p className="text-xs text-white/40 italic" data-id="element-2919">
                                  📝 Nota: {order.notes}
                                </p>
                              </div>}
                          </div>

                          {/* Actions */}
                          <div className="p-3 border-t border-white/5 bg-[#141414]" data-id="element-2920">
                            {caprichoStatus === 'nuevo' && <button onClick={() => handleOrderAction(order.id, 'capricho_receive')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-[#8B5E3C] hover:bg-[#A0704A] text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-2921">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-2922">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2923" />
                                    Procesando...
                                  </span> : '✅ PEDIDO RECIBIDO'}
                              </button>}
                            {caprichoStatus === 'en_preparacion' && <button onClick={() => handleOrderAction(order.id, 'capricho_complete')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-2924">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-2925">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2926" />
                                    Procesando...
                                  </span> : '🎉 PEDIDO COMPLETADO'}
                              </button>}
                            {caprichoStatus === 'listo' && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-2927">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-2928">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2929" />
                                    Procesando...
                                  </span> : '🚀 ENTREGAR'}
                              </button>}
                            {order.delivery_type === 'dine_in' && <button onClick={() => handleOpenPay(order)} disabled={isUpdatingOrder} className="w-full mt-2 h-11 rounded-xl text-sm font-black bg-[#F4D35E] hover:bg-[#FFE08A] text-black flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-2930">
                                <Receipt className="w-4 h-4" data-id="element-2931" />
                                COBRAR / FACTURAR
                              </button>}
                            {['new', 'accepted', 'preparing'].includes(order.status) && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-[10px] font-bold text-white/20 hover:text-red-500 transition-colors mt-2 uppercase tracking-wider" data-id="element-2932">
                                Cancelar pedido
                              </button>}
                          </div>
                        </div>;
            }
            // ── Standard card (non-Capricho) ──
            return <div key={order.id} className={cn('bg-[#141414] rounded-2xl flex flex-col border-l-4 border-y border-r border-white/5 overflow-hidden shadow-xl transition-all', borderColor, waitMinutes >= 15 && order.status !== 'ready' ? 'shadow-[0_0_15px_rgba(239,68,68,0.15)]' : '')} data-id="element-2933">
                        {/* Order Header */}
                        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-2 bg-white/[0.02]" data-id="element-2934">
                          <div className="flex items-center gap-2 min-w-0" data-id="element-2935">
                            <span className="text-lg font-black text-white tabular-nums" data-id="element-2936">
                              #{getShortOrderNumber(order.order_number)}
                            </span>
                            {order.delivery_type === 'delivery' ? <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-2937">
                                <Bike className="w-3 h-3" data-id="element-2938" /> Delivery
                              </span> : order.delivery_type === 'pickup' ? <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-2939">
                                <ShoppingBag className="w-3 h-3" data-id="element-2940" /> Llevar
                              </span> : <span className="bg-white/10 text-white/70 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-2941">
                                <Store className="w-3 h-3" data-id="element-2942" /> Mesa{' '}
                                {order.table_id || ''}
                              </span>}
                          </div>
                          <span className={cn('text-xl font-black tabular-nums tracking-tighter shrink-0', waitMinutes >= 15 && order.status !== 'ready' ? 'text-red-500 animate-pulse' : waitMinutes >= 10 && order.status !== 'ready' ? 'text-amber-500' : 'text-emerald-500')} data-id="element-2943">
                            {waitMinutes}m
                          </span>
                        </div>

                        {/* Customer & Total */}
                        <div className="px-4 py-2 border-b border-white/5 flex justify-between items-center" data-id="element-2944">
                          <p className="text-sm text-white/70 font-bold truncate pr-2" data-id="element-2945">
                            {order.customer_name}
                          </p>
                          <p className="text-sm font-black text-[#F4D35E] shrink-0" data-id="element-2946">
                            ₡{order.total.toLocaleString()}
                          </p>
                        </div>

                        {/* Status Banner — shows current phase. Hidden for Rancho (id=28). */}
                        {!isRanchoSanFrancisco && (order.status === 'preparing' || hasKitchenActivity && order.status !== 'ready') && <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/20" data-id="element-2947">
                              <div className="flex items-center justify-between mb-2" data-id="element-2948">
                                <span className="text-xs font-black text-amber-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-2949">
                                  <ChefHat className="w-3.5 h-3.5" data-id="element-2950" />
                                  {allItemsReady ? 'Cocina terminó' : 'En cocina'}
                                </span>
                                <span className="text-sm font-black text-amber-400 tabular-nums" data-id="element-2951">
                                  {overallPercentage}%
                                </span>
                              </div>
                              <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-2952">
                                <div className={cn('h-full rounded-full transition-all duration-700 ease-out', overallPercentage === 100 ? 'bg-emerald-500' : 'bg-amber-500')} style={{
                    width: `${overallPercentage}%`
                  }} data-id="element-2953" />
                              </div>
                            </div>}

                        {!isRanchoSanFrancisco && (order.status === 'new' || order.status === 'accepted') && !hasKitchenActivity && <div className="px-4 py-3 bg-blue-500/10 border-b border-blue-500/20" data-id="element-2954">
                              <span className="text-xs font-black text-blue-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-2955">
                                <Clock className="w-3.5 h-3.5" data-id="element-2956" />
                                Nuevo pedido — Pendiente de enviar a cocina
                              </span>
                            </div>}

                        {!isRanchoSanFrancisco && order.status === 'ready' && <div className="px-4 py-3 bg-emerald-500/10 border-b border-emerald-500/20" data-id="element-2957">
                            <span className="text-xs font-black text-emerald-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-2958">
                              <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2959" />
                              ¡Listo para entregar!
                            </span>
                          </div>}

                        {/* Station Progress — hidden for Rancho (id=28). */}
                        {!isRanchoSanFrancisco && showProgressView && <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-2960">
                            <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-2961">
                              Progreso por estación
                            </h4>
                            {stationProgress.size === 0 ? <p className="text-sm text-white/40 italic" data-id="element-2962">
                                Sin items
                              </p> : <div className="space-y-3" data-id="element-2963">
                                {Array.from(stationProgress.entries()).map(([code, progress]) => {
                    const isComplete = progress.ready === progress.total;
                    const percentage = Math.round(progress.ready / progress.total * 100);
                    return <div key={code} className="space-y-1.5" data-id="element-2964">
                                        <div className="flex justify-between items-center text-sm" data-id="element-2965">
                                          <span className={cn('font-bold truncate pr-2', isComplete ? 'text-emerald-400' : 'text-white/80')} data-id="element-2966">
                                            {isComplete ? '✅' : '🔥'}{' '}
                                            {progress.name}
                                          </span>
                                          <span className={cn('font-black tabular-nums shrink-0', isComplete ? 'text-emerald-400' : 'text-amber-400')} data-id="element-2967">
                                            {progress.ready} / {progress.total}
                                          </span>
                                        </div>
                                        <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-2968">
                                          <div className={cn('h-full rounded-full transition-all duration-500', isComplete ? 'bg-emerald-500' : 'bg-amber-500')} style={{
                          width: `${percentage}%`
                        }} data-id="element-2969" />
                                        </div>
                                      </div>;
                  })}
                              </div>}
                          </div>}

                        {/* Items list. Rancho (id=28) lo muestra siempre
                            sin etiqueta de estación. El resto mantiene
                            el flujo original. */}
                        {(isRanchoSanFrancisco || (order.status === 'new' || order.status === 'accepted') && !hasKitchenActivity) && <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-2970">
                            <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-2971">
                              Items del pedido
                            </h4>
                            <div className="space-y-1.5" data-id="element-2972">
                              {order.items.filter((item: any) => item.status !== 'cancelled').map((item: any) => <div key={item.id} className="flex justify-between text-sm" data-id="element-2973">
                                    <span className="text-white/70 truncate pr-2" data-id="element-2974">
                                      {item.quantity}x {item.item_name_snapshot}
                                    </span>
                                    {!isRanchoSanFrancisco && <span className="text-white/40 text-xs shrink-0" data-id="element-2975">
                                        {stations.find(s => s.code === item.station_code)?.name || 'General'}
                                      </span>}
                                  </div>)}
                            </div>
                          </div>}

                        {/* Action Footer */}
                        <div className="p-3 border-t border-white/5 bg-[#141414]" data-id="element-2976">
                          {isRanchoSanFrancisco && <div className="flex flex-col gap-2" data-id="element-2977">
                              <button onClick={() => setRanchoModifyOrderId(order.id)} className="w-full h-12 rounded-xl text-sm font-black bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 flex items-center justify-center gap-2 active:scale-95 transition-all" data-id="element-2978">
                                <Pencil className="w-4 h-4" data-id="element-2979" />
                                EDITAR / MODIFICAR ORDEN
                              </button>
                              <button onClick={() => handleImprimirComandaRancho(order)} disabled={printingOrderIds.has(String(order.id))} className="w-full h-12 rounded-xl text-sm font-black bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-2980">
                                {printingOrderIds.has(String(order.id)) ? <span className="flex items-center gap-2" data-id="element-2981">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2982" />
                                    Imprimiendo...
                                  </span> : <>
                                    <Printer className="w-4 h-4" data-id="element-2983" />
                                    IMPRIMIR COMANDA
                                  </>}
                              </button>
                              <button onClick={() => handleOpenPay(order)} className="w-full h-12 rounded-xl text-sm font-black bg-[#F4D35E] hover:bg-[#FFE08A] text-black flex items-center justify-center gap-2 active:scale-95 transition-transform" data-id="element-2984">
                                <Receipt className="w-4 h-4" data-id="element-2985" />
                                COBRAR Y FACTURAR
                              </button>
                            </div>}
                          {!isRanchoSanFrancisco && (order.status === 'new' || order.status === 'accepted') && <button onClick={() => handleOrderAction(order.id, order.status === 'new' ? 'send_to_kitchen' : 'start_preparation')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-amber-500 hover:bg-amber-400 text-black flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-2986">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-2987">
                                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-2988" />
                                    Enviando a cocina...
                                  </span> : '🔥 ENVIAR A COCINA'}
                              </button>}
                          {!isRanchoSanFrancisco && order.status === 'preparing' && <button onClick={() => handleOrderAction(order.id, 'mark_ready')} disabled={isUpdatingOrder || !allItemsReady} className={cn('w-full h-12 rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all', allItemsReady ? 'bg-emerald-500 hover:bg-emerald-400 text-black active:scale-95 shadow-[0_0_15px_rgba(16,185,129,0.2)]' : 'bg-white/5 text-white/30 cursor-not-allowed')} data-id="element-2989">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-2990">
                                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-2991" />
                                    Procesando...
                                  </span> : allItemsReady ? '✅ MARCAR LISTO' : <span className="flex items-center gap-2" data-id="element-2992">
                                    <span className="w-4 h-4 border-2 border-white/20 border-t-amber-400 rounded-full animate-spin" data-id="element-2993" />
                                    EN COCINA — {overallPercentage}% LISTO
                                  </span>}
                              </button>}
                          {!isRanchoSanFrancisco && (order.status === 'ready' || order.status === 'searching_driver') && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-2994">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-2995">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2996" />
                                    Procesando...
                                  </span> : order.delivery_type === 'delivery' ? '🚀 MARCAR COMO ENVIADO' : '🚀 ENTREGAR AL CLIENTE'}
                              </button>}
                          {!isRanchoSanFrancisco && (order.delivery_type === 'dine_in' || isCabanaParrillera && (order.delivery_type === 'delivery' || order.delivery_type === 'pickup')) && <button onClick={() => handleOpenPay(order)} disabled={isUpdatingOrder} className="w-full mt-2 h-11 rounded-xl text-sm font-black bg-[#F4D35E] hover:bg-[#FFE08A] text-black flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-2997">
                                <Receipt className="w-4 h-4" data-id="element-2998" />
                                COBRAR / FACTURAR
                              </button>}
                          {!isRanchoSanFrancisco && ['new', 'accepted', 'preparing'].includes(order.status) && !isCabanaParrillera && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-[10px] font-bold text-white/20 hover:text-red-500 transition-colors mt-2 uppercase tracking-wider" data-id="element-2999">
                                Cancelar pedido
                              </button>}

                          {/* La Cabaña Parrillera — exclusive cashier actions.
                           Add products to the same comanda OR modify/cancel.
                           Available for any active status (incl. ready). */}
                          {isCabanaParrillera && !['cancelled', 'delivered', 'out_for_delivery'].includes(order.status) && <>
                                <button onClick={() => setAddItemsOrderId(order.id)} disabled={isUpdatingOrder} className="w-full mt-2 h-11 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-3000">
                                  ➕ ADICIONAR PRODUCTOS
                                </button>
                                <button onClick={() => setModifyOrderId(order.id)} disabled={isUpdatingOrder} className="w-full mt-2 h-11 rounded-xl text-sm font-black bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-3001">
                                  ✏️ MODIFICAR / CANCELAR
                                </button>
                              </>}
                        </div>
                      </div>;
          }) : <div className="col-span-full py-24 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-2xl border border-white/5 border-dashed" data-id="element-3002">
                    <CheckCircle2 className="w-16 h-16 text-white/10 mb-4" data-id="element-3003" />
                    <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-3004">
                      {activeFilter !== 'all' ? 'Sin pedidos en esta categoría' : 'Todo bajo control'}
                    </h3>
                    <p className="text-sm" data-id="element-3005">
                      {activeFilter !== 'all' ? 'No hay pedidos que coincidan con este filtro.' : 'No hay pedidos activos en este momento.'}
                    </p>
                    {activeFilter !== 'all' && <button onClick={() => setActiveFilter('all')} className="mt-4 px-4 py-2 rounded-lg bg-white/10 text-white/60 text-sm font-bold hover:bg-white/15 transition-colors" data-id="element-3006">
                        Ver todos los pedidos
                      </button>}
                  </div>;
        })()}
        </div>

        {/* PayTableModal */}
        <PayTableModal isOpen={showPayModal} onClose={() => {
        setShowPayModal(false);
        setPayTable(null);
      }} table={payTable} restaurantId={restaurant?.id} onSuccess={allPaid => {
        refetchTables();
        if (allPaid && payTable?.current_order?.id) {
          // Mark the order as delivered when fully paid
          deliverOrder(payTable.current_order.id).then(() => {
            addToast('success', '✅ Pedido cobrado y cerrado');
          });
        } else {
          addToast('success', '💵 Pago parcial registrado');
        }
      }} createdBy={tenantCtx?.userId || null} data-id="element-3007" />

        {/* La Cabaña Parrillera — Modify / Cancel modal */}
        {isCabanaParrillera && <CabanaModifyOrderModal isOpen={!!modifyOrderId} onClose={() => setModifyOrderId(null)} orderId={modifyOrderId} onChanged={() => {
        refetchOrders();
        refetchTables();
      }} onCancelOrder={async orderId => {
        const res = await cancelOrder(orderId);
        if (res.success) {
          addToast('success', '❌ Pedido cancelado');
        } else if (res.error) {
          addToast('error', res.error);
        }
        return res;
      }} data-id="element-3008" />}

        {/* Rancho San Francisco — Editar / Modificar orden (reusa el
            mismo modal genérico de Cabaña). Cancelar la orden completa
            sigue disponible desde acá. */}
        {isRanchoSanFrancisco && <CabanaModifyOrderModal isOpen={!!ranchoModifyOrderId} onClose={() => setRanchoModifyOrderId(null)} orderId={ranchoModifyOrderId} onChanged={() => {
        refetchOrders();
        refetchTables();
      }} onCancelOrder={async orderId => {
        const res = await cancelOrder(orderId);
        if (res.success) {
          addToast('success', '❌ Pedido cancelado');
        } else if (res.error) {
          addToast('error', res.error);
        }
        return res;
      }} data-id="element-3009" />}

        {/* La Cabaña Parrillera — Add items to an existing comanda */}
        {isCabanaParrillera && <CabanaAddItemsModal isOpen={!!addItemsOrderId} onClose={() => setAddItemsOrderId(null)} orderId={addItemsOrderId} restaurantId={restaurant?.id} onAdded={() => {
        addToast('success', 'Productos adicionados');
        refetchOrders();
        refetchTables();
      }} data-id="element-3010" />}

        {isNatucafe && <VoidConfirmModal isOpen={voidModalState.isOpen} onClose={() => setVoidModalState({
        isOpen: false,
        orderId: null,
        originalAmount: 0
      })} onConfirm={handleVoidConfirm} title="Autorizar Anulación de Orden" description="Para anular esta orden completa, ingrese su nombre y el motivo." isSubmitting={updatingOrders.has(voidModalState.orderId || '')} data-id="element-3011" />}

        {/* ═══ TOAST NOTIFICATIONS ═══ */}
        {toasts.length > 0 && <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none" data-id="element-3012">
            {toasts.map(toast => <div key={toast.id} className={cn('pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-bold min-w-[280px] max-w-[400px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-3013">
                <span className="flex-1" data-id="element-3014">{toast.message}</span>
                <button onClick={() => removeToast(toast.id)} className="p-0.5 hover:bg-white/20 rounded transition-colors shrink-0" data-id="element-3015">
                  <X className="w-4 h-4" data-id="element-3016" />
                </button>
              </div>)}
          </div>}
      </div>
    </AdminLayout>;
}